import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { mediaService } from '../../../services/mediaService';
import { authService } from '../../../services/authService';

const MediaUploadCenter = () => {
  const [dragActive, setDragActive] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadMedia();
  }, []);

  const loadMedia = async () => {
    try {
      setLoading(true);
      const data = await mediaService?.getAllMedia();
      setMedia(data || []);
    } catch (err) {
      setError(err?.message || 'Failed to load media');
    } finally {
      setLoading(false);
    }
  };

  const handleDrag = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (e?.type === 'dragenter' || e?.type === 'dragover') {
      setDragActive(true);
    } else if (e?.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = async (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    setDragActive(false);
    
    const files = Array.from(e?.dataTransfer?.files || []);
    if (files?.length > 0) {
      await uploadFiles(files);
    }
  };

  const handleFileSelect = async (e) => {
    const files = Array.from(e?.target?.files || []);
    if (files?.length > 0) {
      await uploadFiles(files);
    }
  };

  const uploadFiles = async (files) => {
    try {
      const user = await authService?.getCurrentUser();
      if (!user) throw new Error('Not authenticated');

      setUploadProgress(0);
      
      for (let i = 0; i < files?.length; i++) {
        const file = files?.[i];
        await mediaService?.uploadFile(file, user?.id);
        setUploadProgress(Math.round(((i + 1) / files?.length) * 100));
      }

      setTimeout(() => {
        setUploadProgress(null);
        loadMedia();
      }, 1000);
    } catch (err) {
      setError(err?.message || 'Upload failed');
      setUploadProgress(null);
    }
  };

  const handleDelete = async (mediaId, filePath) => {
    if (!window.confirm('Are you sure you want to delete this file?')) return;

    try {
      await mediaService?.deleteFile(mediaId, filePath);
      loadMedia();
    } catch (err) {
      setError(err?.message || 'Delete failed');
    }
  };

  const getMediaUrl = (mediaItem) => {
    if (mediaItem?.mime_type?.startsWith('image/')) {
      return mediaService?.getFileUrl(mediaItem?.file_path);
    }
    return null;
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024)?.toFixed(1) + ' KB';
    return (bytes / (1024 * 1024))?.toFixed(1) + ' MB';
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString();
  };

  return (
    <div className="space-y-6">
      {error && (
        <div className="p-4 bg-error/10 border border-error/20 rounded-lg">
          <p className="text-error text-sm">{error}</p>
        </div>
      )}
      {/* Upload Area */}
      <div
        className={`relative rounded-xl border-2 border-dashed transition-all duration-300 ${
          dragActive ? 'border-primary bg-primary/10' : 'border-border/50'
        }`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <div className="p-6 sm:p-12 text-center">
          <div className="w-16 h-16 sm:w-20 sm:h-20 mx-auto mb-4 rounded-full flex items-center justify-center"
            style={{
              background: 'linear-gradient(135deg, var(--color-primary), var(--color-secondary))'
            }}
          >
            <Icon name="Upload" size={32} color="white" />
          </div>
          
          <h3 className="text-lg sm:text-xl font-semibold text-foreground mb-2">
            Drop files here or click to upload
          </h3>
          <p className="text-sm sm:text-base text-muted-foreground mb-6">
            Support for images (JPG, PNG, GIF) and videos (MP4, MOV)
          </p>
          
          <label htmlFor="file-upload">
            <Button iconName="FolderOpen" onClick={() => document.getElementById('file-upload')?.click()} className="touch-manipulation">
              Select Files
            </Button>
          </label>
          <input
            id="file-upload"
            type="file"
            multiple
            accept="image/*,video/*,application/pdf"
            onChange={handleFileSelect}
            className="hidden"
          />
        </div>

        {/* Upload Progress */}
        {uploadProgress !== null && (
          <div className="absolute inset-0 bg-background/95 backdrop-blur-sm rounded-xl flex items-center justify-center">
            <div className="w-64 space-y-4 px-4">
              <div className="flex items-center justify-between text-sm">
                <span className="text-foreground font-medium">Uploading...</span>
                <span className="text-primary font-bold">{uploadProgress}%</span>
              </div>
              <div className="h-2 bg-muted rounded-full overflow-hidden">
                <motion.div
                  className="h-full rounded-full"
                  style={{
                    background: 'linear-gradient(90deg, var(--color-primary), var(--color-secondary))'
                  }}
                  initial={{ width: 0 }}
                  animate={{ width: `${uploadProgress}%` }}
                  transition={{ duration: 0.3 }}
                />
              </div>
            </div>
          </div>
        )}
      </div>
      {/* Media Gallery */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-xl sm:text-2xl font-bold text-foreground">Media Library</h2>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" iconName="Grid" className="touch-manipulation" />
            <Button variant="outline" size="sm" iconName="List" className="touch-manipulation" />
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading media...</p>
          </div>
        ) : media?.length === 0 ? (
          <div className="text-center py-12">
            <Icon name="Image" size={48} color="var(--color-muted-foreground)" />
            <p className="text-muted-foreground mt-4">No media uploaded yet</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            {media?.map((mediaItem) => (
              <MediaCard
                key={mediaItem?.id}
                mediaItem={mediaItem}
                onDelete={handleDelete}
                getMediaUrl={getMediaUrl}
                formatFileSize={formatFileSize}
                formatDate={formatDate}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const MediaCard = ({ mediaItem, onDelete, getMediaUrl, formatFileSize, formatDate }) => {
  const [imageUrl, setImageUrl] = useState(null);

  useEffect(() => {
    const loadImageUrl = async () => {
      if (mediaItem?.mime_type?.startsWith('image/')) {
        try {
          const url = await getMediaUrl(mediaItem);
          setImageUrl(url);
        } catch (err) {
          console.error('Failed to load image URL:', err);
        }
      }
    };
    loadImageUrl();
  }, [mediaItem]);

  return (
    <motion.div
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      className="relative rounded-xl overflow-hidden border border-border/50 backdrop-blur-xl group touch-manipulation"
      style={{
        background: 'linear-gradient(135deg, rgba(30, 41, 59, 0.6), rgba(15, 23, 42, 0.8))'
      }}
    >
      {/* Thumbnail */}
      <div className="aspect-video bg-muted flex items-center justify-center">
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={mediaItem?.file_name}
            className="w-full h-full object-cover"
          />
        ) : (
          <Icon
            name={mediaItem?.mime_type?.startsWith('video/') ? 'Video' : 'File'}
            size={48}
            color="var(--color-muted-foreground)"
          />
        )}
      </div>
      {/* Info */}
      <div className="p-4">
        <p className="text-foreground font-medium truncate mb-1">{mediaItem?.file_name}</p>
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>{formatFileSize(mediaItem?.file_size)}</span>
          <span>{formatDate(mediaItem?.created_at)}</span>
        </div>
      </div>
      {/* Actions Overlay */}
      <div className="absolute inset-0 bg-background/90 backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
        {imageUrl && (
          <a href={imageUrl} target="_blank" rel="noopener noreferrer">
            <Button variant="secondary" size="icon" iconName="Eye" />
          </a>
        )}
        <Button 
          variant="destructive" 
          size="icon" 
          iconName="Trash2"
          onClick={() => onDelete(mediaItem?.id, mediaItem?.file_path)}
        />
      </div>
    </motion.div>
  );
};

export default MediaUploadCenter;